import { useEffect, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useWizardStore } from '../wizardStore';
import { subscriptionApi } from '@/entities/subscription/api';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { WizardFooter } from '@/shared/ui/WizardFooter';
import { SubscriptionIcon } from '@/shared/ui/SubscriptionIcon';
import { useT } from '@/shared/lib/i18n';
import { cn } from '@/shared/lib/cn';

// ─── Types ────────────────────────────────────────────────────────────────────

interface IconItem {
  slug: string;
  title: string;
  aliases: string[];
  categories: string[];
  hex: string | null;
  variants: string[];
}

type Tab = 'services' | 'emoji';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function preferredVariant(variants: string[]): string {
  return variants.includes('default') ? 'default' : variants[0] ?? 'default';
}

function isServiceIconSlug(icon: string): boolean {
  return icon.length > 0 && /^[a-z0-9][a-z0-9-]*$/.test(icon);
}

// ─── Hook: keeps previous items visible while loading new ones ────────────────

function useIconSearch(q: string, enabled: boolean) {
  const [items, setItems] = useState<IconItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || !q.trim()) {
      setItems([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q: q.trim(), limit: '48' });
        const res = await fetch(`/api/v1/icons?${params}`, { signal: controller.signal });
        if (!res.ok) return;
        const data = await res.json();
        setItems(data.items ?? []);
      } catch {
        // abort or network error — keep previous items
      } finally {
        setLoading(false);
      }
    }, 280);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [q, enabled]);

  return { items, loading };
}

// ─── Emoji data ───────────────────────────────────────────────────────────────

interface EmojiBaseItem {
  group?: number;
  label?: string;
  order?: number;
  unicode: string;
}

interface EmojiCategory {
  key: string;
  emoji: string[];
}

let emojiCategoriesCache: EmojiCategory[] | null = null;

function buildEmojiCategories(data: EmojiBaseItem[]): EmojiCategory[] {
  const emojiItems = data
    .filter((item) => item.unicode && typeof item.group === 'number' && item.group !== 2)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const byGroup = (group: number) => emojiItems
    .filter((item) => item.group === group)
    .map((item) => item.unicode);

  return [
    { key: 'all', emoji: emojiItems.map((item) => item.unicode) },
    { key: 'faces', emoji: byGroup(0) },
    { key: 'people', emoji: byGroup(1) },
    { key: 'animals', emoji: byGroup(3) },
    { key: 'food', emoji: byGroup(4) },
    { key: 'travel', emoji: byGroup(5) },
    { key: 'activity', emoji: byGroup(6) },
    { key: 'objects', emoji: byGroup(7) },
    { key: 'symbols', emoji: byGroup(8) },
    { key: 'flags', emoji: byGroup(9) },
  ];
}

function useEmojiCategories(enabled: boolean) {
  const [categories, setCategories] = useState<EmojiCategory[]>(() => emojiCategoriesCache ?? []);

  useEffect(() => {
    if (!enabled || categories.length > 0) return;

    let mounted = true;
    void import('emojibase-data/en/compact.json').then((module) => {
      const next = buildEmojiCategories(module.default as EmojiBaseItem[]);
      emojiCategoriesCache = next;
      if (mounted) setCategories(next);
    });

    return () => {
      mounted = false;
    };
  }, [categories.length, enabled]);

  return categories;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function NameStep() {
  const { name, icon, type, update, next } = useWizardStore();
  const t = useT();
  const [tab, setTab] = useState<Tab>('services');
  const [emojiCat, setEmojiCat] = useState(0);
  const emojiCategories = useEmojiCategories(tab === 'emoji');

  const createDraftMutation = useMutation({
    mutationFn: () =>
      subscriptionApi.create({
        name,
        icon,
        type: type as 'subscription' | 'collection',
        total_price: 0,
        currency: 'RUB',
        period: { interval: 1, unit: 'months' },
        billing_date: null,
        deadline_shift: 0,
        split_mode: 'equal',
        privacy: 'everyone',
        show_history: false,
        draft_step: 'price',
        member_slots: [],
      }),
    onSuccess: (sub) => {
      update({ subscriptionId: sub.id });
      next();
    },
  });

  const { items: serviceIcons, loading } = useIconSearch(name, tab === 'services');

  const autoLetter = name.trim() ? name.trim()[0].toUpperCase() : '?';
  const isSlug = isServiceIconSlug(icon);
  const isEmoji = icon.length > 0 && !isSlug;

  const emojiLabels: Record<string, string> = {
    all: t('wizard.name.emojiCatAll'),
    faces: t('wizard.name.emojiCatFaces'),
    people: t('wizard.name.emojiCatPeople'),
    animals: t('wizard.name.emojiCatAnimals'),
    food: t('wizard.name.emojiCatFood'),
    travel: t('wizard.name.emojiCatTravel'),
    activity: t('wizard.name.emojiCatActivity'),
    objects: t('wizard.name.emojiCatObjects'),
    symbols: t('wizard.name.emojiCatSymbols'),
    flags: t('wizard.name.emojiCatFlags'),
  };

  const currentEmoji = emojiCategories[emojiCat]?.emoji ?? [];

  return (
    // h-full so we fill the flex-1 parent and can have an internal scrollable area
    <div className="flex flex-col h-full w-full min-w-0 gap-3">
      {/* Title — fixed */}
      <h1 className="text-[24px] font-bold tracking-tight flex-shrink-0">
        {t('wizard.name.title')}
      </h1>

      {/* Name input + icon preview — fixed */}
      <div className="flex items-center gap-3 flex-shrink-0 min-w-0">
        <div
          className={cn('flex-shrink-0 relative', icon ? 'cursor-pointer' : 'cursor-default')}
          onClick={() => icon && update({ icon: '' })}
          role={icon ? 'button' : undefined}
          aria-label={icon ? t('wizard.name.resetIcon') : undefined}
        >
          {isEmoji ? (
            <span className="w-[52px] h-[52px] rounded-[14px] bg-brand-gradient flex items-center justify-center text-[28px] shadow-[0_8px_18px_rgba(18,120,90,0.3)] leading-none select-none">
              {icon}
            </span>
          ) : isSlug ? (
            <img
              src={`/api/v1/icons/${icon}/default`}
              alt={icon}
              className="w-[52px] h-[52px] rounded-[14px] object-contain bg-white/60 border border-white/60 p-1"
            />
          ) : (
            <SubscriptionIcon
              letter={autoLetter}
              size="lg"
              className="!w-[52px] !h-[52px] !rounded-[14px] !text-[22px]"
            />
          )}
          {icon && (
            <span className="absolute -top-1 -right-1 w-[18px] h-[18px] rounded-full bg-white shadow-sm flex items-center justify-center text-[11px] text-text-secondary leading-none select-none">
              ×
            </span>
          )}
        </div>

        <input
          className="flex-1 min-w-0 rounded-[14px] px-[13px] py-[13px] text-[18px] font-semibold bg-white/55 border-2 border-brand-green/40 outline-none focus:border-brand-green transition-colors"
          placeholder={t('wizard.name.placeholder')}
          value={name}
          onChange={(e) => update({ name: e.target.value })}
          autoFocus
        />
      </div>

      {/* Tab switcher — fixed */}
      <div className="flex rounded-[10px] bg-black/[0.06] p-[3px] flex-shrink-0">
        {(['services', 'emoji'] as const).map((t_) => (
          <button
            key={t_}
            onClick={() => setTab(t_)}
            className={cn(
              'flex-1 py-[7px] text-[13px] font-semibold rounded-[8px] transition-all',
              tab === t_
                ? 'bg-white text-text-primary shadow-sm'
                : 'text-text-secondary',
            )}
          >
            {t_ === 'services' ? t('wizard.name.tabServices') : t('wizard.name.tabEmoji')}
          </button>
        ))}
      </div>

      {/* ── Icon area ────────────────────────────────────────────────────── */}
      {tab === 'services' && (
        <div className="flex-1 overflow-y-auto overflow-x-hidden liquid-scrollbar min-h-0 px-1 pt-1.5 pb-2">
          <div
            className={cn(
              'grid grid-cols-4 gap-3 content-start transition-opacity duration-200',
              loading && 'opacity-60 pointer-events-none',
            )}
          >
            {/* Auto option */}
            <button
              className={cn(
                'rounded-[14px] p-2 flex items-center justify-center transition-all active:scale-95',
                !icon
                  ? 'ring-2 ring-brand-green shadow-[0_5px_14px_rgba(18,120,90,0.22)]'
                  : 'hover:scale-[1.03]',
              )}
              onClick={() => update({ icon: '' })}
              aria-label={t('wizard.name.autoOption')}
            >
              <SubscriptionIcon
                letter={autoLetter}
                size="sm"
                className="!w-[40px] !h-[40px] !rounded-[10px] !text-[18px]"
              />
            </button>

            {serviceIcons.map((item) => {
              const v = preferredVariant(item.variants);
              const isSelected = icon === item.slug;
              return (
                <button
                  key={item.slug}
                  className={cn(
                    'rounded-[14px] p-2 flex items-center justify-center transition-all active:scale-95',
                    isSelected
                      ? 'ring-2 ring-brand-green shadow-[0_5px_14px_rgba(18,120,90,0.22)]'
                      : 'hover:scale-[1.03]',
                  )}
                  onClick={() => update({ icon: item.slug })}
                  aria-label={item.title}
                >
                  <img
                    src={`/api/v1/icons/${item.slug}/${v}`}
                    alt={item.title}
                    className="w-[42px] h-[42px] object-contain"
                    loading="lazy"
                  />
                </button>
              );
            })}

            {!loading && serviceIcons.length === 0 && (
              <p className="col-span-4 text-center text-[14px] text-text-muted py-8">
                {name.trim() ? t('wizard.name.noResults') : t('wizard.name.searchHint')}
              </p>
            )}
          </div>
        </div>
      )}

      {tab === 'emoji' && (
        <div className="flex-1 min-h-0 min-w-0 flex flex-col gap-3 overflow-hidden">
          {/* Category rail stays pinned; only the emoji grid below scrolls vertically. */}
          <div className="w-full min-w-0 overflow-x-auto overflow-y-hidden pb-0.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden flex-shrink-0">
            <div className="flex gap-1.5 w-max max-w-none">
              {emojiCategories.map((category, idx) => (
                <button
                  key={category.key}
                  onClick={() => setEmojiCat(idx)}
                  className={cn(
                    'px-3 py-[5px] rounded-full text-[12px] font-semibold whitespace-nowrap transition-all',
                    emojiCat === idx
                      ? 'bg-brand-gradient text-white shadow-[0_3px_8px_rgba(18,120,90,0.3)]'
                      : 'bg-white/60 text-text-secondary hover:bg-white/80',
                  )}
                >
                  {emojiLabels[category.key] ?? category.key}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden liquid-scrollbar px-1 pt-1.5 pb-2">
            <div className="grid grid-cols-8 gap-2 content-start">
              {emojiCategories.length === 0 && (
                <p className="col-span-8 text-center text-[14px] text-text-muted py-8">
                  {t('common.loading')}
                </p>
              )}
              {currentEmoji.map((emoji, idx) => (
                <button
                  key={`${emoji}-${idx}`}
                  className={cn(
                    'aspect-square rounded-[10px] flex items-center justify-center text-[23px] transition-all active:scale-90',
                    icon === emoji
                      ? 'ring-2 ring-brand-green shadow-[0_5px_14px_rgba(18,120,90,0.22)]'
                      : 'hover:scale-[1.06]',
                  )}
                  onClick={() => update({ icon: emoji })}
                  aria-label={emoji}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Footer button via portal */}
      <WizardFooter>
        {createDraftMutation.isError && (
          <p className="text-[13px] text-status-debt text-center">
            {t('wizard.preview.error')}
          </p>
        )}
        <PrimaryButton
          onClick={() => createDraftMutation.mutate()}
          disabled={!name.trim()}
          loading={createDraftMutation.isPending}
        >
          {t('wizard.continue')}
        </PrimaryButton>
      </WizardFooter>
    </div>
  );
}
